import { Component, computed, effect, inject, input, OnInit } from '@angular/core';
import { outputFromObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { hasError } from '../../../shared/helpers/has-error.func';
import { customPhoneValidator } from '../../../shared/validators/custom-phone-validator';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { LvrInputDirective } from '../../directives/lvr-input.directive';
import { LvrField } from '../field/field';
import { LvrSearchableSelect } from '../searchable-select/searchable-select';

@Component({
	selector: 'lvr-contact-point',
	templateUrl: './contact-point.html',
	imports: [
		LvrInputDirective,
		ReactiveFormsModule,
		TranslocoPipe,
		MatFormFieldModule,
		MatInput,
		LvrField,
		MatCheckbox,
		LvrSearchableSelect,
	],
})
export class LvrContactPoint implements OnInit {
	private readonly fb = inject(FormBuilder);
	private readonly env = inject(ENVIRONMENT);
	private readonly transloco = inject(TranslocoService);
	private readonly contactInfoTitle = toSignal(this.transloco.selectTranslate('base.field.contact_info'), {
		initialValue: 'base.field.contact_info',
	});
	public readonly contactPointCategories = input.required<ContactPointCategory[]>();
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly initValue = input<ContactPoint>();
	public readonly form = this.fb.group({
		contactValue: ['', [Validators.required]],
		type: this.fb.control<number | undefined>(undefined),
		preferred: [false],
		remark: [''],
	});
	private readonly currentFormData = toSignal(this.form.valueChanges.pipe(startWith(this.form.value)), {
		initialValue: this.form.value,
	});

	private readonly typeSig = computed(() => {
		const data = this.currentFormData();
		return data?.type ?? this.initValue()?.categoryContactPointId ?? undefined;
	});

	public readonly contactValueTitle = computed(() => {
		const contactValue = this.transloco.translate('base.field.contact_value');
		const phoneNumberLabel = this.transloco.translate('base.field.phone_number');
		const type = this.typeSig();
		if (type == null) return this.contactInfoTitle();

		const category = (this.contactPointCategories() ?? []).find((c) => c.id === type);
		if (category?.remark.toLocaleLowerCase() === 'telefon') return phoneNumberLabel;
		return category?.remark ?? contactValue;
	});

	public readonly valid = outputFromObservable(
		this.form.valueChanges.pipe(
			distinctUntilChanged(),
			debounceTime(200),
			filter(() => this.form.valid),
			map(() => this.form.value),
			map((vals) => !!vals?.contactValue),
		),
	);
	public readonly values = outputFromObservable<{ valid: boolean; value: ContactPoint }>(
		this.form.valueChanges.pipe(
			distinctUntilChanged(),
			debounceTime(200),
			map(() => {
				return {
					value: {
						id: this.initValue()?.id ?? -1,
						personId: this.initValue()?.personId ?? -1,
						personTypeId: this.initValue()?.personTypeId ?? -1,
						categoryContactPointId: this.form.get('type')?.value ?? 0,
						contactValue: this.form.get('contactValue')?.value ?? '',
						preferredWayOfContact: this.form.get('preferred')?.value ?? false,
						remark: this.form.get('remark')?.value ?? '',
					},
					valid: this.form.valid && !!this.form.get('contactValue')?.value,
				};
			}),
		),
	);

	public constructor() {
		effect(() => {
			const type = this.typeSig();
			this.updateContactValueValidator(type);
		});
	}

	public ngOnInit() {
		if (!this.initValue()) return;
		this.form.setValue(
			{
				contactValue: this.initValue()?.contactValue ?? '',
				type: this.initValue()?.categoryContactPointId,
				preferred: this.initValue()?.preferredWayOfContact ?? false,
				remark: this.initValue()?.remark ?? '',
			},
			{
				emitEvent: false,
			},
		);
	}

	private updateContactValueValidator(categoryId?: number | null) {
		if (!categoryId) {
			this.form.get('contactValue')?.setValidators([]);
			return;
		}
		const category = (this.contactPointCategories() ?? []).find((c) => c.id === categoryId);
		if (category?.initial.toLowerCase() === 'tel') {
			this.form.get('contactValue')?.setValidators([customPhoneValidator()]);
		} else if (category?.initial.toLowerCase() === 'mail') {
			this.form.get('contactValue')?.setValidators([Validators.pattern(this.env.emailRegexStr)]);
		} else {
			this.form.get('contactValue')?.setValidators([]);
		}
		this.form.get('contactValue')?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
	}
}

export interface ContactPointValue {
	id?: number;
	categoryId: number;
	contactValue: string;
	preferred: boolean;
	valid: boolean;
	remark?: string;
}
